import InfoRounded from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip';
import { CharactersCounter } from '@openx/components-core/lib/CharactersCounter/CharactersCounter';
import { RHFCheckboxField } from '@openx/components-core/lib/CheckboxField/RHFCheckboxField';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import { theme } from '@openx/components-core/lib/theme/theme';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { ACL_TYPE } from '@openx/types/aclType';
import { CuratedPackageAction, PackageFields } from '@openx/types/package';
import { UserRole } from '@openx/types/user';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePackageContext } from '../../../utils/context/PackageContext';
import { PACKAGE_DESCRIPTION, packageLabels } from '../constants';
import { PackageEstimatedRangesSection } from './PackageEstimatedRangesSection';

export function DetailsSection() {
  const { t } = useTranslation();
  const { useCurrentInstanceState } = useTargetingContext();
  const { usePackagePermissions } = usePackageContext();
  const { isBefInstance } = useCurrentInstanceState();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed, accessUser } = useIsAllowed();
  const observableDescription = useWatch({ name: PackageFields.DESCRIPTION });
  const observableIsCurated: string = useWatch({
    name: PackageFields.IS_CURATED,
  });

  const { hasCuratedPermission } = usePackagePermissions();

  const displayLockedInternalCheckbox =
    isBefInstance &&
    (isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE)) ||
      accessUser?.roles.includes(UserRole.NETWORK_OA_OPENX_BUYER));

  const isCurator = useMemo(
    () => hasCuratedPermission({ action: CuratedPackageAction.UPDATE, pckg: { is_curated: true } }),
    [hasCuratedPermission]
  );

  return (
    <FormSection
      divider
      title={t('Package Details')}
      description={PACKAGE_DESCRIPTION(t)}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
      name="Details"
    >
      {(displayLockedInternalCheckbox || isCurator) && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {displayLockedInternalCheckbox && (
            <RHFCheckboxField
              name={PackageFields.LOCKED_INTERNAL}
              label={
                <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row', width: '100%' }}>
                  <Typography>{packageLabels(t)[PackageFields.LOCKED_INTERNAL]}</Typography>
                  <AlertTooltip
                    placement="top"
                    color={theme.palette.text.secondary}
                    icon={<InfoRounded />}
                    title={t('This package will only be editable by OpenX internal users.')}
                  />
                </Box>
              }
              data-test="locked-internal"
            />
          )}

          {isCurator && (
            <RHFCheckboxField
              name={PackageFields.IS_CURATED}
              label={
                <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row', width: '100%' }}>
                  <Typography>{packageLabels(t)[PackageFields.IS_CURATED]}</Typography>
                  <AlertTooltip
                    placement="top"
                    color={theme.palette.text.secondary}
                    icon={<InfoRounded />}
                    title={t('A curated package is a package that is only editable by OpenX Package Curators.')}
                  />
                </Box>
              }
              data-test="package-is-curated"
            />
          )}
        </Box>
      )}

      <RHFTextField
        name={PackageFields.NAME}
        label={packageLabels(t)[PackageFields.NAME]}
        placeholder={t('Type your Package Name')}
        fullWidth
        dataTest="package-name"
      />
      <RHFTextField
        name={PackageFields.DESCRIPTION}
        label={packageLabels(t)[PackageFields.DESCRIPTION]}
        placeholder={t('Enter Package description here')}
        multiline
        rows={1}
        fullWidth
        margin="dense"
        slotProps={{ htmlInput: { maxLength: 255 }, formHelperText: { component: 'div' } }}
        helperText={<CharactersCounter maxValue={255} currentValue={observableDescription?.length} />}
        dataTest="package-description"
      />

      {isCurator && observableIsCurated && <PackageEstimatedRangesSection />}
    </FormSection>
  );
}
